import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { ChartCard } from '@/components/dashboard/ChartCard';
import {
  MONTH_LABELS,
  formatAxis,
  orderedLegendContent,
  plnTooltipFormatter,
} from '@/components/dashboard/chart-utils';
import { t } from '@/i18n';
import type { ProjectMonthlyPoint } from '@ip/shared-types';

interface ProjectMonthlyChartProps {
  monthly: ProjectMonthlyPoint[];
}

/**
 * "YYYY-MM" → "Sep '25"-style label. The rolling 12-month window crosses a
 * year boundary, so the label carries a short year (unlike the CFO dashboard
 * charts, which are year-scoped and label with the bare month).
 */
function monthLabel(month: string): string {
  const [year, monthNo] = month.split('-');
  const index = Number(monthNo) - 1;
  const name = MONTH_LABELS[index] ?? month;
  return `${name} '${year.slice(2)}`;
}

/**
 * Task B3: grouped cost-vs-revenue bars for a project — same conventions as
 * the CFO dashboard's RevenueCostChart (ONE PLN axis, revenue var(--chart-1),
 * cost var(--chart-4), pinned legend order).
 */
export function ProjectMonthlyChart({ monthly }: ProjectMonthlyChartProps) {
  const chartData = monthly.map((m) => ({
    month: monthLabel(m.month),
    revenue: m.revenueNet,
    cost: m.costNet,
  }));

  const config: ChartConfig = {
    revenue: { label: t('dashboard.financial.revenue'), color: 'var(--chart-1)' },
    cost: { label: t('dashboard.financial.costs'), color: 'var(--chart-4)' },
  };

  return (
    <ChartCard title={t('projects.analytics.monthlyTitle')}>
      <ChartContainer config={config} className="h-[300px] w-full">
        <BarChart data={chartData} barGap={2}>
          <CartesianGrid vertical={false} strokeOpacity={0.4} />
          <XAxis dataKey="month" tickLine={false} axisLine={false} />
          <YAxis tickFormatter={formatAxis} tickLine={false} axisLine={false} width={64} />
          <ChartTooltip content={<ChartTooltipContent formatter={plnTooltipFormatter(config)} />} />
          <ChartLegend content={orderedLegendContent(['revenue', 'cost'])} />
          <Bar dataKey="revenue" fill="var(--color-revenue)" radius={[4, 4, 0, 0]} maxBarSize={24} />
          <Bar dataKey="cost" fill="var(--color-cost)" radius={[4, 4, 0, 0]} maxBarSize={24} />
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}
